import { Extension } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { handlePostEditorBackspace } from '../lib/post_editor_backspace.js';
import { handleResourceRowEnter } from '../lib/container_paragraph_keys.js';
import {
  findResourceRowDepth,
  isResourceRowNode,
  paragraphRowRole,
  resourceRowHasSubtitle,
  resourceRowSubtitleInsertPos,
  resourceRowSubtitleTextPos,
  RESOURCE_ROW_SUBTITLE_ADDED_META,
  RESOURCE_ROW_SUBTITLE_PARAGRAPH_ATTRS,
  sanitizeResourceRows,
} from '../lib/resource_row_paragraph.js';

const resourceRowRoleSyncKey = new PluginKey('resourceRowRoleSync');

function syncResourceRowRoles(tr, doc) {
  let modified = false;

  doc.descendants((node, pos) => {
    if (!isResourceRowNode(node)) return;

    node.forEach((child, offset) => {
      if (child.type.name !== 'paragraph') return;

      const role = paragraphRowRole(child);
      if (child.attrs.psRowRole === role) return;

      tr.setNodeMarkup(pos + 1 + offset, undefined, {
        ...child.attrs,
        psRowRole: role,
      });
      modified = true;
    });
  });

  return modified;
}

export const ResourceRowParagraphBehavior = Extension.create({
  name: 'resourceRowParagraphBehavior',

  priority: 10001,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: resourceRowRoleSyncKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          let subtitleRowPos = null;
          for (const transaction of transactions) {
            const rowPos = transaction.getMeta(RESOURCE_ROW_SUBTITLE_ADDED_META);
            if (typeof rowPos === 'number') subtitleRowPos = rowPos;
          }

          const tr = newState.tr;
          const rolesSynced = syncResourceRowRoles(tr, tr.doc);
          const rowsSanitized = sanitizeResourceRows(tr, tr.doc);
          if (!rolesSynced && !rowsSanitized && subtitleRowPos == null) return null;

          if (subtitleRowPos != null) {
            const selPos = resourceRowSubtitleTextPos(tr.doc, subtitleRowPos);
            if (selPos != null) {
              tr.setSelection(TextSelection.create(tr.doc, selPos));
            }
          }
          return tr;
        },
      }),
    ];
  },

  addCommands() {
    return {
      addResourceRowSubtitle:
        (rowPos) =>
        ({ state, dispatch, editor }) => {
          const { selection } = state;
          let resolvedRowPos = typeof rowPos === 'number' ? rowPos : null;
          let rowNode =
            resolvedRowPos != null ? state.doc.nodeAt(resolvedRowPos) : null;

          if (!rowNode || !isResourceRowNode(rowNode)) {
            if (selection instanceof NodeSelection && isResourceRowNode(selection.node)) {
              resolvedRowPos = selection.from;
              rowNode = selection.node;
            } else {
              const rowDepth = findResourceRowDepth(selection.$from);
              if (rowDepth < 0) return false;
              resolvedRowPos = selection.$from.before(rowDepth);
              rowNode = selection.$from.node(rowDepth);
            }
          }

          if (!rowNode || resourceRowHasSubtitle(rowNode)) return false;

          const paragraphType = state.schema.nodes.paragraph;
          if (!paragraphType) return false;

          const insertPos = resourceRowSubtitleInsertPos(resolvedRowPos, rowNode);
          const subtitlePara = paragraphType.create(RESOURCE_ROW_SUBTITLE_PARAGRAPH_ATTRS);

          if (dispatch) {
            const tr = state.tr.insert(insertPos, subtitlePara);
            tr.setSelection(TextSelection.create(tr.doc, insertPos + 1));
            tr.setMeta(RESOURCE_ROW_SUBTITLE_ADDED_META, resolvedRowPos);
            dispatch(tr);

            // Re-apply after appendTransaction sanitize/role sync and after focus() restores DOM selection.
            if (editor && !editor.isDestroyed) {
              const rowPosForSel = resolvedRowPos;
              requestAnimationFrame(() => {
                if (editor.isDestroyed) return;
                const selPos = resourceRowSubtitleTextPos(editor.state.doc, rowPosForSel);
                if (selPos == null) return;
                editor.chain().setTextSelection(selPos).focus().run();
              });
            }
          }
          return true;
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        if (!selection.empty) return false;
        return handleResourceRowEnter(editor, state, selection.$from);
      },

      'Shift-Enter': ({ editor }) => editor.commands.setHardBreak(),

      Backspace: ({ editor }) => handlePostEditorBackspace(editor.view, { key: 'Backspace' }),
    };
  },
});
